/**
 * Assistant message display helpers.
 *
 * Shared classification for protocol-only assistant payloads so live WS events
 * and chat.history render consistently.
 */

export type AssistantMessageKind =
  | 'message'
  | 'no_reply'
  | 'heartbeat'
  | 'blank';

export interface AssistantMessageSummary {
  trimmed: string;
  preview: string;
  length: number;
}

/**
 * Summarize assistant content for compact logging.
 */
export function summarizeAssistantMessage(content: string): AssistantMessageSummary {
  const trimmed = content.trim();
  return {
    trimmed,
    preview: trimmed.substring(0, 120),
    length: content.length,
  };
}

/**
 * Classify assistant content for display.
 *
 * Blank assistant finals are treated as protocol acknowledgements unless the
 * caller knows the message also contains tool-call UI state.
 */
export function getAssistantMessageKind(
  content: string,
  options?: { hasToolCalls?: boolean },
): AssistantMessageKind {
  const trimmed = content.trim();

  if (trimmed === 'HEARTBEAT_OK') return 'heartbeat';
  if (trimmed === 'NO_REPLY') return 'no_reply';
  if (!trimmed) return options?.hasToolCalls ? 'message' : 'blank';
  return 'message';
}

/**
 * Final-only active-session events should only render when they contain a real
 * assistant message. Protocol acknowledgements stay suppressed.
 */
export function shouldRenderFinalOnlyAssistantMessage(kind: AssistantMessageKind): boolean {
  return kind === 'message';
}

/**
 * Protocol-only history artifacts should stay hidden to avoid ghost bubbles.
 */
export function shouldHideHistoryAssistantMessage(kind: AssistantMessageKind): boolean {
  return kind === 'heartbeat' || kind === 'blank';
}
